import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../context/AuthContext";
import AppShell from "../components/AppShell";
import SEO from "../components/SEO";
import { ArrowLeft, EyeOff, Trophy, TrendingUp, Package, Loader2, Copy, Check } from "lucide-react";

export default function Profile() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/profile/${encodeURIComponent(name)}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [name]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (notFound) {
    return (
      <AppShell>
        <div className="pt-16 text-center">
          <div className="text-[13px] text-white/40">Not found</div>
          <div className="mt-2 text-3xl font-bold">No player named &quot;{name}&quot;.</div>
          <Link to="/" className="mt-6 inline-block text-white/70 hover:text-white">Back home</Link>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;
  }

  if (data.private) {
    return (
      <AppShell>
        <div className="pt-16 px-6 flex items-center justify-center">
          <div className="glass rounded-3xl p-10 max-w-md w-full text-center">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              <EyeOff className="w-5 h-5 text-white/60" />
            </div>
            <h1 className="mt-5 text-3xl font-bold">{data.display_name}</h1>
            <p className="mt-2 text-white/60 text-[14px]">This profile is private.</p>
            <Link to="/" className="mt-6 inline-block text-white/70 hover:text-white">Back home</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  const p = data.player;
  const s = data.stats;
  const big = data.biggest_hit;

  return (
    <AppShell>
      <SEO
        title={`${p.display_name} — player profile`}
        description={`${p.display_name} on Gumble: ${s.total_bets} verified bets, ${s.total_crates} crates, biggest hit ${big ? `${Number(big.multiplier || 0).toFixed(1)}x on ${big.game}` : "—"}.`}
        path={`/p/${encodeURIComponent(p.stake_username || p.username || name)}`}
        image={p.avatar}
      />
      <div className="pt-10 md:pt-14 px-6 md:px-10 max-w-5xl mx-auto pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-[13px] text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</Link>

        {/* Header */}
        <div className="mt-6 glass rounded-3xl p-8 flex flex-col md:flex-row items-start md:items-center gap-6 relative overflow-hidden">
          <div className="absolute -top-24 -right-16 w-72 h-72 bg-white/[0.04] blur-3xl rounded-full pointer-events-none" />
          {p.avatar ? (
            <img src={p.avatar} alt="" className="w-24 h-24 rounded-3xl border border-white/10" />
          ) : (
            <div className="w-24 h-24 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-3xl font-black text-white/60">
              {(p.display_name || "?")[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <div className="text-[12px] uppercase tracking-widest text-white/40">Player</div>
            <h1 className="mt-1 text-4xl md:text-5xl font-black tracking-[-0.02em]">{p.display_name}</h1>
            {p.stake_username && <div className="mt-2 text-[14px] text-white/60">Stake: @{p.stake_username}</div>}
          </div>
          <div className="flex gap-2">
            <button onClick={copyLink} className="inline-flex items-center gap-2 rounded-full glass glass-hover px-4 py-2 text-[13px]">
              {copied ? <><Check className="w-4 h-4 text-emerald-300" /> Copied</> : <><Copy className="w-4 h-4" /> Share</>}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Bets approved" value={s.total_bets} />
          <StatCard label="Crates earned" value={s.total_crates} />
          <StatCard label="Total tips" value={`$${(s.total_reward_usd || 0).toFixed(2)}`} accent />
          <StatCard label="Weekly rank" value={s.weekly_rank ? `#${s.weekly_rank}` : "—"} />
        </div>

        {/* Biggest hit */}
        {big && (
          <div className="mt-6 glass rounded-3xl p-7">
            <div className="text-[12px] uppercase tracking-widest text-white/40 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Biggest hit
            </div>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <div className="text-5xl font-black tracking-[-0.03em] text-white">{(big.multiplier || 0).toFixed(1)}x</div>
              <div className="text-white/70 text-[15px]">on <b className="text-white">{big.game}</b></div>
              <div className="ml-auto text-[14px] text-white/70">Payout <b className="text-white">${(big.payout || 0).toFixed(2)}</b></div>
            </div>
          </div>
        )}

        {/* Top bets */}
        <div className="mt-6">
          <h2 className="text-2xl font-bold tracking-tight">Top bets</h2>
          <div className="mt-4 glass rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead className="text-[11px] uppercase tracking-widest text-white/40">
                  <tr>
                    <th className="text-left p-4 w-10">#</th>
                    <th className="text-left p-4">Game</th>
                    <th className="text-right p-4">Wager</th>
                    <th className="text-right p-4">Multiplier</th>
                    <th className="text-right p-4">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.top_bets || []).length === 0 && <tr><td colSpan={5} className="p-5 text-white/40">No bets yet.</td></tr>}
                  {(data.top_bets || []).map((b, i) => (
                    <tr key={b.bet_id + i} className="border-t hairline">
                      <td className="p-4 text-white/50">{i + 1}</td>
                      <td className="p-4">{b.game}</td>
                      <td className="p-4 text-right">${(b.wager || 0).toFixed(2)}</td>
                      <td className="p-4 text-right text-white inline-flex items-center gap-1 justify-end w-full"><TrendingUp className="w-3 h-3" />{(b.multiplier || 0).toFixed(1)}x</td>
                      <td className="p-4 text-right">${(b.payout || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="text-[11px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`mt-2 text-3xl font-black tracking-tight text-white`}>{value}</div>
    </div>
  );
}
